import React, { useState } from 'react';
import { Upload, Download, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import api from '../lib/api';
import { toast } from 'sonner';

export const ImportExcelDialog = ({
  open,
  onOpenChange,
  onSuccess,
  title = "Importar desde Excel",
  description = "Carga un archivo Excel (.xlsx) para importar datos masivamente",
  downloadTemplateEndpoint,
  templateFilename = "plantilla.xlsx",
  uploadEndpoint,
  identifierLabel = "Registro",
  identifierField = "name"
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls')) {
        setFile(droppedFile);
        setResults(null);
      } else {
        toast.error('Por favor selecciona un archivo Excel (.xlsx, .xls)');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setResults(null);
      } else {
        toast.error('Por favor selecciona un archivo Excel (.xlsx, .xls)');
      }
    }
  };

  const handleDownloadTemplate = async () => {
    if (!downloadTemplateEndpoint) return;
    
    try {
      const response = await api.get(downloadTemplateEndpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', templateFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Plantilla descargada exitosamente');
    } catch (error) {
      toast.error('Error al descargar la plantilla');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    if (!uploadEndpoint) {
      toast.error('Endpoint de carga no configurado');
      return;
    }

    setUploading(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResults(response.data);
      
      if (response.data.successful > 0) {
        toast.success(`${response.data.successful} registros importados exitosamente`);
        if (onSuccess) onSuccess();
      }
      
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} registros fallaron`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar datos');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Download Template Button */}
          {downloadTemplateEndpoint && (
            <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-[10px]">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">¿Primera vez?</p>
                <p className="text-xs text-slate-600 mt-1">Descarga la plantilla Excel de ejemplo</p>
              </div>
              <Button
                type="button"
                onClick={handleDownloadTemplate}
                variant="outline"
                className="border-[#2537b0] text-[#2537b0] hover:bg-blue-50"
              >
                <Download size={16} className="mr-2" />
                Descargar Plantilla
              </Button>
            </div>
          )}

          {/* File Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-[10px] p-8 text-center transition-colors ${
              dragActive
                ? 'border-[#2537b0] bg-blue-50'
                : 'border-[#e0e0e0] bg-white hover:border-[#2537b0]/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
            />
            
            <Upload size={48} className="mx-auto text-slate-400 mb-4" />
            
            {file ? (
              <div>
                <p className="text-sm font-medium text-slate-900 mb-1">{file.name}</p>
                <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-red-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setResults(null);
                  }}
                >
                  Eliminar archivo
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-slate-900 mb-1">
                  Arrastra tu archivo Excel (.xlsx) aquí
                </p>
                <p className="text-xs text-slate-500">o haz clic para seleccionar</p>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Procesando archivo...</span>
              </div>
              <Progress value={50} className="h-2" />
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 border border-[#e0e0e0] rounded-[10px] text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{results.total}</p>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-[10px] text-center">
                  <CheckCircle size={20} className="mx-auto text-green-600 mb-1" />
                  <p className="text-xs font-semibold uppercase tracking-wider text-green-600 mb-1">Exitosas</p>
                  <p className="text-2xl font-bold text-green-700">{results.successful}</p>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-[10px] text-center">
                  <XCircle size={20} className="mx-auto text-red-600 mb-1" />
                  <p className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-1">Fallidas</p>
                  <p className="text-2xl font-bold text-red-700">{results.failed}</p>
                </div>
              </div>

              {/* Error Details */}
              {results.errors && results.errors.length > 0 && (
                <div className="max-h-60 overflow-y-auto bg-red-50 border border-red-200 rounded-[10px] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle size={18} className="text-red-600" />
                    <h4 className="text-sm font-semibold text-red-900">Errores Detectados</h4>
                  </div>
                  <div className="space-y-2">
                    {results.errors.map((error, index) => (
                      <div key={index} className="text-xs bg-white p-2 rounded border border-red-200">
                        <p className="font-medium text-red-900">
                          Fila {error.row}: {error[identifierField] || 'Sin identificador'}
                        </p>
                        <p className="text-red-700 mt-1">{error.error}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={uploading}>
            {results ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!results && (
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white"
            >
              {uploading ? 'Importando...' : 'Importar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
